/** Strip ANSI escape codes from error strings */
const stripAnsi = (str) => {
  if (typeof str !== "string") return str;
  return str.replace(
    /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g,
    ""
  );
};

export const formatFailureTrace = (trace) => {
  if (!trace) return "";
  if (Array.isArray(trace)) return trace.map((t) => stripAnsi(String(t))).join("\n");
  return stripAnsi(String(trace));
};

export const isStepFailed = (status) => {
  if (!status) return false;
  const normalized = String(status).toLowerCase();
  return normalized === "failed" || normalized === "timedout" || normalized === "interrupted";
};

export const isStepPassed = (status) => {
  if (!status) return false;
  const normalized = String(status).toLowerCase();
  return normalized === "passed";
};

export const normalizeErrorMessage = (message, maxLength = 100) => {
  if (!message) return "Unknown Error";
  let errMsg = typeof message === "string" ? message : JSON.stringify(message);
  errMsg = stripAnsi(errMsg);
  return errMsg
    .split("\n")[0]
    .replace(/[0-9a-fA-F-]{36}/g, "...")
    .replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d+Z/g, "...")
    .substring(0, maxLength);
};

const findFailedStep = (steps) => {
  if (!steps?.length) return null;
  return (
    steps.find((s) => isStepFailed(s.status)) ||
    steps.find((s) => s.errorMessage) ||
    null
  );
};

const getTestLevelLabel = (testCase) =>
  testCase.testCaseSummary ||
  testCase.scenarioName ||
  testCase.testCaseId ||
  "Test failure";

/**
 * Resolve failure metadata from steps when present, otherwise from test-level fields.
 */
export const getFailureContext = (testCase) => {
  const failedStep = findFailedStep(testCase.steps);
  const traceText = formatFailureTrace(testCase.failureTrace);
  const testLogs =
    testCase.logs ||
    (testCase.steps || [])
      .map((s) => s.logs)
      .filter(Boolean)
      .join("\n");

  if (failedStep) {
    const errMsg = failedStep.errorMessage || traceText || "Unknown Error";
    return {
      failedStepName: failedStep.name || "Unknown Step",
      shortMsg: normalizeErrorMessage(errMsg),
      fullError: typeof errMsg === "string" ? stripAnsi(errMsg) : JSON.stringify(errMsg),
      logs: failedStep.logs || testLogs,
    };
  }

  const failedStepName = getTestLevelLabel(testCase);
  const fullError = traceText || testCase.errorMessage || "No trace available";
  return {
    failedStepName,
    shortMsg: normalizeErrorMessage(fullError),
    fullError,
    logs: testLogs,
  };
};

/**
 * Steps for UI: real steps when available, otherwise a single synthetic failed step from test data.
 */
export const getDisplaySteps = (testCase) => {
  if (testCase.steps?.length > 0) {
    return testCase.steps;
  }

  const traceText = formatFailureTrace(testCase.failureTrace);
  if (!traceText && !testCase.errorMessage) {
    return [];
  }

  return [
    {
      name: getTestLevelLabel(testCase),
      status: "failed",
      errorMessage: traceText || testCase.errorMessage || "",
    },
  ];
};

export const formatLogsForDisplay = (logs) => {
  if (!logs) return "";
  if (typeof logs === "string") return logs;
  if (typeof logs === "object") return JSON.stringify(logs, null, 2);
  return String(logs);
};
